import MarkdownIt from 'markdown-it';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no types available
import attrs from 'markdown-it-attrs';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no types available
import deflist from 'markdown-it-deflist';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no types available
import footnote from 'markdown-it-footnote';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no types available
import container from 'markdown-it-container';

// All supported admonition types
const ADMONITION_TYPES = [
  'note', 'abstract', 'info', 'tip', 'success', 'question',
  'warning', 'failure', 'danger', 'bug', 'example', 'quote',
];

const ADMONITION_COLORS: Record<string, string> = {
  note: '#448aff',
  abstract: '#00b0ff',
  info: '#00b8d4',
  tip: '#00bfa5',
  success: '#00c853',
  question: '#64dd17',
  warning: '#ff6d00',
  failure: '#ff1744',
  danger: '#ff1744',
  bug: '#f50057',
  example: '#7c4dff',
  quote: '#9e9e9e',
};

const ADMONITION_ICONS: Record<string, string> = {
  note: '📝',
  abstract: '📋',
  info: 'ℹ️',
  tip: '💡',
  success: '✅',
  question: '❓',
  warning: '⚠️',
  failure: '❌',
  danger: '🔥',
  bug: '🐛',
  example: '📖',
  quote: '💬',
};

function createAdmonitionPlugin(type: string) {
  return [
    type,
    {
      validate: (params: string) => params.trim().startsWith(type),
      render: (tokens: Array<{ nesting: number; info: string }>, idx: number) => {
        const token = tokens[idx];
        if (token.nesting === 1) {
          const params = token.info.trim().slice(type.length).trim();
          const title = params.replace(/^"(.*)"$/, '$1') || type.charAt(0).toUpperCase() + type.slice(1);
          const color = ADMONITION_COLORS[type] || '#448aff';
          const icon = ADMONITION_ICONS[type] || '📝';
          return `<div class="admonition admonition-${type}" style="border-left: 4px solid ${color}; padding: 1em; margin: 1em 0; background: ${color}11; border-radius: 4px;">
            <div class="admonition-title" style="color: ${color}; font-weight: bold; margin-bottom: 0.5em;">${icon} ${title}</div>
            <div class="admonition-body">`;
        }
        return '</div></div>\n';
      },
    },
  ] as [string, object];
}

export function createMarkdownRenderer(): MarkdownIt {
  const md = new MarkdownIt({
    html: true,
    linkify: true,
    typographer: false,
  });

  // Plugins
  md.use(attrs);
  md.use(deflist);
  md.use(footnote);

  // Admonition containers
  for (const type of ADMONITION_TYPES) {
    const [name, opts] = createAdmonitionPlugin(type);
    md.use(container, name, opts);
  }

  // Caption block: /// caption | Optional title  (pymdownx.blocks.caption syntax)
  // marker:'/' makes markdown-it-container look for '///' delimiters
  md.use(container, 'caption', {
    marker: '/',
    validate: (params: string) => params.trim().startsWith('caption'),
    render: (tokens: Array<{ nesting: number; info: string }>, idx: number) => {
      const token = tokens[idx];
      if (token.nesting === 1) {
        const rest = token.info.trim().slice('caption'.length).replace(/^\s*\|\s*/, '').trim();
        if (rest) {
          return `<figure>\n<figcaption>${md.utils.escapeHtml(rest)}</figcaption>\n`;
        }
        return '<figure>\n';
      }
      return '</figure>\n';
    },
  });

  // Material icons :material-xxx: → span (inline rule)
  md.inline.ruler.push('material_icons', (state, silent) => {
    const src = state.src;
    const pos = state.pos;

    if (src[pos] !== ':') return false;

    const match = src.slice(pos).match(/^:([a-zA-Z][-a-zA-Z0-9_]*(?:[-_][a-zA-Z0-9_]+)*):/);
    if (!match) return false;

    if (!silent) {
      const token = state.push('html_inline', '', 0);
      token.content = `<span class="material-icon" title="${match[1]}">:${match[1]}:</span>`;
    }
    state.pos += match[0].length;
    return true;
  });

  return md;
}

export async function renderMarkdown(
  content: string,
  md: MarkdownIt
): Promise<string> {
  const html = md.render(content);
  return html;
}
